import { useMemo, useState } from 'react';
import { Check, Fingerprint, ArrowUpRight, Phone, Undo2, Hand, Clock, ThumbsDown, X } from 'lucide-react';
import { Select, SelectContentHighPriority, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { GuiaBiometriaAceite } from './GuiaBiometriaAceite';
import { toast } from '@/hooks/use-toast';
import { ehDoSegmento, rotuloSegmento, useSegmentoTriagem } from '@/components/kanban/triagem/segmentoTriagem';
import { TriagemShell } from '@/components/kanban/triagem/TriagemShell';
import { TRIAGEM_TH } from '@/components/kanban/triagem/triagemTabela';
import { useLinhasOcupadas } from '@/components/kanban/triagem/acaoTriagem';
import { useTriagemBiometria } from './useTriagemBiometria';
import { TriagemBiometriaRow } from './TriagemBiometriaRow';
import { MarcarNaoFeitoDialog } from './MarcarNaoFeitoDialog';
import { ManterFilaDialog } from '@/components/kanban/triagem/ManterFilaDialog';
import type { PedidoTriagemBiometria } from './useTriagemBiometria';

interface TriagemBiometriaModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function TriagemBiometriaModal({ isOpen, onClose }: TriagemBiometriaModalProps) {
  const [guiaAberto, setGuiaAberto] = useState(false);
  const {
    pedidos: pedidosTodos,
    isLoading,
    refetch,
    invalidateAllQueries,
    enviarParaAceite,
    voltarParaCriacao,
    alternarSinalizacao,
    marcarNaoFeito,
    manterNoFim,
    podeAcoesBko,
    podeSinalizar,
  } = useTriagemBiometria();
  /* Só o pedido clicado fica ocupado; o resto da fila continua livre
     (`triagem/acaoTriagem.ts`). A sinalização tem a sua própria marca. */
  const { ocupada, ocupar } = useLinhasOcupadas();
  const { ocupada: sinalizando, ocupar: sinalizar } = useLinhasOcupadas();

  /* ⛔ CNPJ e CPF NÃO SE MISTURAM NA FILA (Douglas 17/09/2026). A régua de
     entrada desta triagem é a MESMA de sempre — o que muda é a fila mostrar um
     segmento por vez, e os contadores do topo falarem do que está na tela.
     A separação é filtro, nunca coluna nova: nada troca de fila fora da
     triagem. Lei em `triagem/segmentoTriagem.ts`. */
  const { segmento, setSegmento } = useSegmentoTriagem();
  const pedidos = useMemo(
    () => pedidosTodos.filter((r) => ehDoSegmento(r.tipo_pessoa, segmento)),
    [pedidosTodos, segmento],
  );

  const [pedidoNaoFeito, setPedidoNaoFeito] = useState<PedidoTriagemBiometria | null>(null);
  const [pedidoManter, setPedidoManter] = useState<PedidoTriagemBiometria | null>(null);
  const [equipe, setEquipe] = useState('TODAS');
  /* Douglas 19/08: os selos viraram FILTRO. É por eles que se decide o que
     conferir agora — "o comercial disse que fez" e "eu conferi e não foi". */
  const [sinal, setSinal] = useState<'feito' | 'nao_feito' | null>(null);

  /* Filtro por EQUIPE (Douglas 19/08): a Bio tem 100+ pedidos e ninguém — nem
     gerente, nem BKO, nem a supervisora da Qualidade — conseguia olhar só a sua
     equipe. As opções saem do que a pessoa já enxerga, então o filtro não abre
     visão nenhuma: só estreita. */
  const equipes = useMemo(
    () => Array.from(new Set(pedidos.map((p) => p.equipe_nome).filter(Boolean))).sort(),
    [pedidos],
  );
  const daEquipe = useMemo(
    () => (equipe === 'TODAS' ? pedidos : pedidos.filter((p) => p.equipe_nome === equipe)),
    [pedidos, equipe],
  );

  /* Os contadores dos selos são SEMPRE da equipe inteira. Se caíssem junto com o
     filtro, clicar em "sinalizados" zeraria "não feitos" e não teria como pular
     de um para o outro. */
  const totalSinalizados = daEquipe.filter((p) => p.sinalizado_em && p.sinal_estado !== 'nao_feito').length;
  const totalNaoFeito = daEquipe.filter((p) => p.sinal_estado === 'nao_feito').length;

  const visiveis = useMemo(() => {
    if (sinal === 'feito') return daEquipe.filter((p) => p.sinalizado_em && p.sinal_estado !== 'nao_feito');
    if (sinal === 'nao_feito') return daEquipe.filter((p) => p.sinal_estado === 'nao_feito');
    return daEquipe;
  }, [daEquipe, sinal]);

  const totalItens = visiveis.reduce((sum, p) => sum + p.itens.length, 0);
  /* Duas moedas, NUNCA somadas (Douglas 17/08/2026: "nao e junto, e separado"):
     o contador de R$ da fila e FATURAMENTO, e aparelho anda em selo proprio.
     Junto, os dois aparelhos da fila (R$ 16.398,00) eram 56% do total e faziam a
     Bio parecer estar segurando um faturamento que ela nao segura. Somam sobre
     `visiveis` para acompanhar o filtro de equipe, igual aos outros contadores. */
  const totalValue = visiveis.reduce((sum, p) => sum + p.valor_total, 0);
  const totalAparelhos = visiveis.reduce((sum, p) => sum + p.qtd_aparelhos, 0);

  return (
    <TriagemShell
      isOpen={isOpen}
      onFechar={onClose}
      Icone={Fingerprint}
      titulo="Triagem Biometria"
      contadores={{ pedidos: visiveis.length, itens: totalItens, valor: totalValue, aparelhos: totalAparelhos }}
      segmento={segmento}
      onSegmento={setSegmento}
      sla={null}
      permitirPrint
      cabecalhoExtra={
        <span className="flex items-center gap-2">
          {equipes.length > 1 && (
            <Select value={equipe} onValueChange={setEquipe}>
              {/* MESMA régua dos outros selos do cabeçalho (h-7, rounded-lg,
                  border-border, bg-card, 10.5px): o padrão do SelectTrigger é
                  de formulário — rounded-md, border-input, bg-background — e
                  ficava com cara de campo velho no meio das pílulas. */}
              <SelectTrigger className="h-7 w-auto min-w-[132px] flex-shrink-0 gap-1 rounded-lg border-border bg-card px-2.5 text-[10.5px] font-semibold text-muted-foreground [&>svg]:h-3 [&>svg]:w-3 [&>svg]:opacity-70">
                <SelectValue placeholder="Equipe" />
              </SelectTrigger>
              <SelectContentHighPriority>
                <SelectItem value="TODAS">Todas as equipes</SelectItem>
                {equipes.map((e) => (
                  <SelectItem key={e} value={e}>{e}</SelectItem>
                ))}
              </SelectContentHighPriority>
            </Select>
          )}
          {totalSinalizados > 0 && (
            /* "O comercial já marcou como feito": é o que diz à BKO o que
               conferir primeiro — e agora filtra a tela nele. */
            <button
              type="button"
              onClick={() => setSinal((atual) => (atual === 'feito' ? null : 'feito'))}
              className={`flex h-7 flex-shrink-0 items-center gap-1 rounded-lg border px-2.5 text-[10.5px] font-semibold text-primary transition-colors ${
                sinal === 'feito' ? 'border-primary bg-accent' : 'border-border bg-card hover:bg-muted'
              }`}
              title={sinal === 'feito'
                ? 'Mostrando só os sinalizados — clique para voltar à fila inteira'
                : 'Ver só os pedidos que o comercial marcou como feitos'}
            >
              <Hand className="h-3 w-3" />
              {totalSinalizados} sinalizado{totalSinalizados !== 1 ? 's' : ''}
            </button>
          )}
          {totalNaoFeito > 0 && (
            <button
              type="button"
              onClick={() => setSinal((atual) => (atual === 'nao_feito' ? null : 'nao_feito'))}
              className={`flex h-7 flex-shrink-0 items-center gap-1 rounded-lg border px-2.5 text-[10.5px] font-semibold text-primary transition-colors ${
                sinal === 'nao_feito' ? 'border-primary bg-accent' : 'border-border bg-card hover:bg-muted'
              }`}
              title={sinal === 'nao_feito'
                ? 'Mostrando só os não feitos — clique para voltar à fila inteira'
                : 'Ver só os que o BKO conferiu e não foram feitos (o consultor já foi avisado)'}
            >
              <ThumbsDown className="h-3 w-3" />
              {totalNaoFeito} não feito{totalNaoFeito !== 1 ? 's' : ''}
            </button>
          )}
        </span>
      }
      fifo={false}
      legenda={[
        { key: 'manter', Icone: Clock, label: 'Manter (fim da fila)' },
        { key: 'cobrar-ligar-whatsapp', Icone: Phone, label: 'Cobrar (ligar / WhatsApp)' },
        { key: 'timeline', Icone: Clock, label: 'Timeline' },
        { key: 'analisar', Icone: ArrowUpRight, label: 'Analisar' },
        { key: 'j-fiz-avisa-o-bko-n-o-sai-da-fila', Icone: Hand, label: 'Já fiz — avisa o BKO (não sai da fila)' },
        { key: 'comercial-sinalizou', Icone: Hand, label: 'Comercial sinalizou' },
        { key: 'nao-feito', Icone: ThumbsDown, label: 'Não feito (avisa o consultor)' },
        { key: 'voltar-p-cria-o', Icone: Undo2, label: 'Voltar p/ Criação' },
        { key: 'aceite', Icone: Check, label: 'Aceite' },
        { key: 'cancelar', Icone: X, label: 'Cancelar' },
      ]}
      isLoading={isLoading}
      onAtualizar={async () => { await refetch(); toast({ title: 'Dados atualizados', duration: 1500 }); }}
      vazio={
        totalItens === 0
          ? (sinal || equipe !== 'TODAS'
              ? {
                  titulo: 'Nenhum pedido com esse filtro',
                  descricao: 'Clique de novo no selo (ou volte para "Todas as equipes") para ver a fila inteira.',
                }
              : {
                  titulo: 'Nenhum pedido pendente',
                  descricao: 'Todos os pedidos da biometria foram processados',
                })
          : undefined
      }
    >
            <table className="w-full" style={{ tableLayout: 'fixed' }}>
              <thead className="sticky top-0 z-10 bg-background border-b border-border">
                <tr className="text-left text-xs text-muted-foreground uppercase tracking-wider">
                  <th className={`${TRIAGEM_TH} w-[44px]`}>Fila</th>
                  <th className={`${TRIAGEM_TH} w-[128px]`}>{rotuloSegmento(segmento)}</th>
                  <th className={`${TRIAGEM_TH}`}>Razão Social</th>
                  <th className={`${TRIAGEM_TH}`}>Consultor</th>
                  <th className={`${TRIAGEM_TH} w-[96px]`}>BKO</th>
                  <th className={`${TRIAGEM_TH} w-[88px]`}>Pedido</th>
                  <th className={`${TRIAGEM_TH}`}>Produto</th>
                  <th className={`${TRIAGEM_TH} w-[42px]`}>QTD</th>
                  <th className={`${TRIAGEM_TH} w-[84px]`}>Valor</th>
                  <th className={`${TRIAGEM_TH} w-[72px]`}>Tempo</th>
                  <th className={`${TRIAGEM_TH} w-[76px]`}>Sinal</th>
                  <th className={`${TRIAGEM_TH} w-[128px]`}>Contato</th>
                  <th className={`${TRIAGEM_TH} w-[300px]`}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {visiveis.map((pedido, index) => (
                  <TriagemBiometriaRow
                    key={pedido.pedido_id}
                    pedido={pedido}
                    posicao={index}
                    isProcessing={ocupada(pedido.pedido_id)}
                    podeAcoesBko={podeAcoesBko}
                    podeSinalizar={podeSinalizar}
                    isSinalizando={sinalizando(pedido.pedido_id)}
                    onVoltarCriacao={(p) => { void ocupar(p.pedido_id, () => voltarParaCriacao(p)).catch(() => undefined); }}
                    onEnviarAceite={(p) => { void ocupar(p.pedido_id, () => enviarParaAceite(p)).catch(() => undefined); }}
                    onAlternarSinalizacao={(p) => { void sinalizar(p.pedido_id, () => alternarSinalizacao(p)).catch(() => undefined); }}
                    onMarcarNaoFeito={(p) => {
                      // Já marcado: o clique limpa direto, sem pedir recado de novo
                      if (p.sinal_estado === 'nao_feito') { void sinalizar(p.pedido_id, () => marcarNaoFeito({ pedido: p })).catch(() => undefined); return; }
                      setPedidoNaoFeito(p);
                    }}
                    onManterFimFila={setPedidoManter}
                    onAfterCancel={invalidateAllQueries}
                  />
                ))}
              </tbody>
            </table>
        <MarcarNaoFeitoDialog
          open={!!pedidoNaoFeito}
          onOpenChange={(aberto) => { if (!aberto) setPedidoNaoFeito(null); }}
          numeroPedido={pedidoNaoFeito?.numero_pedido || ''}
          razaoSocial={pedidoNaoFeito?.razao_social || ''}
          consultorNome={pedidoNaoFeito?.consultor_nome || ''}
          sinalizadoPor={pedidoNaoFeito?.sinalizado_por || null}
          isLoading={!!pedidoNaoFeito && sinalizando(pedidoNaoFeito.pedido_id)}
          onConfirm={async (recado) => {
            if (!pedidoNaoFeito) return;
            try {
              await sinalizar(pedidoNaoFeito.pedido_id, () => marcarNaoFeito({ pedido: pedidoNaoFeito, recado }));
              setPedidoNaoFeito(null);
            } catch { /* toast no hook */ }
          }}
        />

        <ManterFilaDialog
          open={!!pedidoManter}
          onOpenChange={(aberto) => { if (!aberto) setPedidoManter(null); }}
          numeroPedido={pedidoManter?.numero_pedido || ''}
          razaoSocial={pedidoManter?.razao_social || ''}
          isLoading={!!pedidoManter && ocupada(pedidoManter.pedido_id)}
          onConfirm={async ({ justificativa, prd }) => {
            if (!pedidoManter) return;
            try {
              await ocupar(pedidoManter.pedido_id, () => manterNoFim({ pedido: pedidoManter, justificativa, prd }));
              setPedidoManter(null);
            } catch { /* toast no hook */ }
          }}
        />

        <GuiaBiometriaAceite open={guiaAberto} onOpenChange={setGuiaAberto} />
    </TriagemShell>
  );
}
